import type { Runtime } from '@yada/runtime';

export const PASSIVE_CONTEXT_PREFIX = 'Live application context (refreshed for this request):\n';

/** Collect app-owned context, plus the shell's public navigation state.
 * Never scrape inputs, document bodies, arbitrary storage, or other apps.
 */
export async function collectPassiveAppContext(runtime: Runtime, appId: string): Promise<string> {
  const active = runtime.active;
  const instance = active?.id === appId ? active.instance : undefined;
  const scope = () => JSON.stringify([
    localStorage.getItem('yada:account:activeAccountId'),
    localStorage.getItem('yada:account:activeProfileId'),
    localStorage.getItem('yada:currentWorkspaceId'),
  ]);
  const originalScope = scope();
  const context = instance?.getAiContext
    ? await instance.getAiContext()
    : await runtime.importAiCapabilities(appId);
  if (scope() !== originalScope || runtime.active !== active) {
    throw new Error('The account, profile, workspace, or active app changed while loading AI context. Submit the request again.');
  }
  const appName = runtime.list().find(app => app.id === appId)?.name ?? appId;
  const lines = [`Application: ${appName} (${appId})`];
  if (instance) {
    const path = instance.getCurrentPath?.();
    if (path) lines.push(`Current entity path: ${path}`);
    const header = typeof document === 'undefined' ? null : document.getElementById('app-header');
    const tabs = Array.from(header?.querySelectorAll('[role="tab"][aria-selected="true"]') ?? [])
      .map(tab => tab.textContent?.trim()).filter(Boolean);
    if (tabs.length) lines.push(`Active view: ${tabs.join(', ')}`);
  } else {
    lines.push('This application has no mounted view.');
  }
  if (context?.currentContext) lines.push(context.currentContext);
  else lines.push('No additional app context is provided. Use available read tools for details.');
  return lines.join('\n\n');
}
